import * as React from 'react';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Check,
  X,
  Globe,
} from 'lucide-react';
import { api, Language, CreateLanguageData, UpdateLanguageData, ApiError } from '@/lib/api';
import { Card, CardContent, Button, Badge } from '@/components/ui';

interface LanguageFormData {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  isActive: boolean;
}

export default function LanguagesPage() {
  const [search, setSearch] = React.useState('');
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const [languages, setLanguages] = React.useState<Language[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState<string | null>(null);
  const [isToggling, setIsToggling] = React.useState<string | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editingLanguage, setEditingLanguage] = React.useState<Language | null>(null);
  const [formData, setFormData] = React.useState<LanguageFormData>({
    code: '',
    name: '',
    nativeName: '',
    flag: '',
    isActive: true,
  });
  const [isSaving, setIsSaving] = React.useState(false);

  const loadLanguages = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getLanguages();
      setLanguages(data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao carregar idiomas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadLanguages();
  }, [loadLanguages]);

  const handleDelete = async (id: string, name: string) => {
    if (
      !confirm(
        `Tem certeza que deseja excluir o idioma "${name}"? Esta ação não pode ser desfeita.`
      )
    ) {
      return;
    }

    try {
      setIsDeleting(id);
      await api.deleteLanguage(id);
      setLanguages((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao excluir idioma');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      setIsToggling(id);
      const updated = await api.toggleLanguageActive(id);
      setLanguages((prev) =>
        prev.map((l) => (l.id === id ? updated : l))
      );
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao alterar status do idioma');
    } finally {
      setIsToggling(null);
    }
  };

  const openCreateModal = () => {
    setEditingLanguage(null);
    setFormData({
      code: '',
      name: '',
      nativeName: '',
      flag: '',
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (language: Language) => {
    setEditingLanguage(language);
    setFormData({
      code: language.code,
      name: language.name,
      nativeName: language.nativeName,
      flag: language.flag,
      isActive: language.isActive,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingLanguage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSaving(true);

      if (editingLanguage) {
        // Update
        const updated = await api.updateLanguage(editingLanguage.id, formData);
        setLanguages((prev) =>
          prev.map((l) => (l.id === editingLanguage.id ? updated : l))
        );
      } else {
        // Create
        const created = await api.createLanguage(formData as CreateLanguageData);
        setLanguages((prev) => [...prev, created]);
      }

      closeModal();
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao salvar idioma');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredLanguages = languages
    .filter((lang) => includeInactive || lang.isActive)
    .filter(
      (lang) =>
        lang.name.toLowerCase().includes(search.toLowerCase()) ||
        lang.nativeName.toLowerCase().includes(search.toLowerCase()) ||
        lang.code.toLowerCase().includes(search.toLowerCase())
    );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Idiomas</h1>
          <p className="text-gray-500 mt-1">
            Gerencie o catálogo global de idiomas disponíveis no sistema
          </p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Idioma
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome, nome nativo ou código..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF]"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeInactive"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[#0057FF] focus:ring-[#0057FF]"
              />
              <label htmlFor="includeInactive" className="text-sm text-gray-700">
                Incluir inativos
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent className="p-12">
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Languages Grid */}
      {!isLoading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLanguages.map((language) => (
            <Card key={language.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="text-4xl">{language.flag}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {language.name}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        {language.nativeName}
                      </p>
                      <p className="text-xs text-gray-400 font-mono">
                        {language.code}
                      </p>
                    </div>
                  </div>
                  {language.isActive ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Check className="h-3 w-3 mr-1" />
                      Ativo
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-50 text-gray-700 border-gray-200">
                      <X className="h-3 w-3 mr-1" />
                      Inativo
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(language)}
                    className="flex-1"
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(language.id)}
                    disabled={isToggling === language.id}
                    className="flex-1"
                  >
                    {isToggling === language.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : language.isActive ? (
                      <X className="h-4 w-4 mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    {language.isActive ? 'Desativar' : 'Ativar'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(language.id, language.name)}
                    disabled={isDeleting === language.id}
                    className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                  >
                    {isDeleting === language.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && filteredLanguages.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {search
                ? 'Nenhum idioma encontrado com os filtros aplicados'
                : 'Nenhum idioma cadastrado'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                {editingLanguage ? 'Editar Idioma' : 'Novo Idioma'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="pt-BR, en-US, es-ES..."
                    value={formData.code}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, code: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Português (Brasil)"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Nativo *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Português (Brasil)"
                    value={formData.nativeName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        nativeName: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emoji da Bandeira *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="🇧🇷"
                    value={formData.flag}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, flag: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF]"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isActive: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-[#0057FF] focus:ring-[#0057FF]"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    Idioma ativo
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeModal}
                    disabled={isSaving}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : editingLanguage ? (
                      'Salvar Alterações'
                    ) : (
                      'Criar Idioma'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
